import { Plus, RotateCcw, Trash2 } from 'lucide-react'
import useStore from '@/stores/useStore'
import { Button } from '@/components/ui/button'
import { CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CompactToggle } from '@/components/ui/toggle-setting'
import PlanCardShell from '@/components/plan/PlanCardShell'

function VestingScheduleSelector({ label, schedule, onChange }) {
  const resetInputValidation = useStore((state) => state.resetInputValidation)
  const handleTypeChange = (type) => {
    if (type === 'immediate') onChange({ type: 'immediate' })
    else if (type === 'cliff') onChange({ type: 'cliff', years: 3 })
    else if (type === 'graded') onChange({ type: 'graded', schedule: [{ years: 0, percent: 0 }, { years: 1, percent: 0 }, { years: 2, percent: 20 }, { years: 3, percent: 40 }, { years: 4, percent: 60 }, { years: 5, percent: 80 }, { years: 6, percent: 100 }] })
  }
  const handleCliffYearsChange = (years) => onChange({ type: 'cliff', years: Math.max(1, parseInt(years) || 1) })
  const updateGradedEntry = (index, field, value) => { const next = [...schedule.schedule]; next[index] = { ...next[index], [field]: parseFloat(value) || 0 }; onChange({ type: 'graded', schedule: next }) }
  const addGradedEntry = () => { const last = schedule.schedule[schedule.schedule.length - 1]; onChange({ type: 'graded', schedule: [...schedule.schedule, { years: (last?.years || 0) + 1, percent: Math.min(100, (last?.percent || 0) + 20) }] }) }
  const removeGradedEntry = (index) => { if (schedule.schedule.length <= 1) return; onChange({ type: 'graded', schedule: schedule.schedule.filter((_, entryIndex) => entryIndex !== index) }) }
  const resetGradedSchedule = () => {
    onChange({ type: 'graded', schedule: [{ years: 0, percent: 0 }, { years: 1, percent: 0 }, { years: 2, percent: 20 }, { years: 3, percent: 40 }, { years: 4, percent: 60 }, { years: 5, percent: 80 }, { years: 6, percent: 100 }] })
    resetInputValidation()
  }

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between">
        <Label className="text-muted-foreground">{label}</Label>
        <div className="flex items-center gap-2">
          <Select value={schedule.type} onValueChange={handleTypeChange}>
            <SelectTrigger aria-label={`${label} schedule type`} className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="immediate">Immediate</SelectItem>
              <SelectItem value="cliff">Cliff</SelectItem>
              <SelectItem value="graded">Graded</SelectItem>
            </SelectContent>
          </Select>
          {schedule.type === 'cliff' && (
            <div className="flex items-center gap-1">
              <NumberInput aria-label={`${label} cliff years`} value={schedule.years || 3} onValueChange={handleCliffYearsChange} className="h-8 w-16" min={1} max={10} integer showError={false} emptyMessage="Required." />
              <span className="text-sm text-muted-foreground">years</span>
            </div>
          )}
        </div>
      </div>
      {schedule.type === 'graded' && schedule.schedule && (
        <div className="ml-4 space-y-2 border-l-2 border-muted pl-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Vesting schedule</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={resetGradedSchedule}><RotateCcw className="mr-1 h-3 w-3" />Restore default schedule</Button>
              <Button size="sm" className="h-6 px-2 text-xs" onClick={addGradedEntry}><Plus className="mr-1 h-3 w-3" />Add year</Button>
            </div>
          </div>
          {schedule.schedule.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="w-8 text-xs text-muted-foreground">Year</span>
              <NumberInput aria-label={`${label} year ${index + 1}`} value={entry.years} onValueChange={(value) => updateGradedEntry(index, 'years', value)} className="h-7 w-16 text-xs" min={0} max={30} integer showError={false} emptyMessage="Required." />
              <span className="text-xs text-muted-foreground">:</span>
              <NumberInput aria-label={`${label} vested percentage for row ${index + 1}`} value={entry.percent} onValueChange={(value) => updateGradedEntry(index, 'percent', value)} className="h-7 w-16 text-xs" min={0} max={100} showError={false} emptyMessage="Required." />
              <span className="text-xs text-muted-foreground">%</span>
              {schedule.schedule.length > 1 && <Button aria-label={`Remove ${label} row ${index + 1}`} variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive" onClick={() => removeGradedEntry(index)}><Trash2 className="h-3 w-3" /></Button>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function VestingCard({ id, title, description, reference, vesting, matchEnabled, necEnabled, necIsSafeHarbor, profitSharingEnabled, onEnabledChange, onMatchScheduleChange, onNecScheduleChange, onPsScheduleChange, disabled, variant }) {
  return (
    <PlanCardShell
      id={id}
      disabled={disabled}
      variant={variant}
      title={title}
      description={description}
      titleAction={() => onEnabledChange(!vesting.enabled)}
      titleActionLabel="Toggle vesting schedule"
      titleActionDisabled={disabled}
      headerAction={<CompactToggle label="Use a vesting schedule" checked={vesting.enabled} onCheckedChange={onEnabledChange} disabled={disabled} />}
      collapsible={false}
    >
      {(reference || vesting.enabled) && <CardContent className="space-y-4">
        {reference}
        {vesting.enabled && (
          <div className="mt-1 space-y-3">
            {!matchEnabled && !necEnabled && !profitSharingEnabled ? (
              <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
                No employer contribution sources are enabled, so there’s nothing to vest yet. Turn on match, nonelective, or profit sharing to set a schedule.
              </div>
            ) : (
              <>
                {matchEnabled && <VestingScheduleSelector label="Match vesting" schedule={vesting.matchSchedule} onChange={onMatchScheduleChange} />}
                {necEnabled && (necIsSafeHarbor ? (
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <Label className="text-muted-foreground">NEC vesting</Label>
                      <p className="text-xs text-muted-foreground">Safe harbor requires immediate vesting</p>
                    </div>
                    <span className="text-sm font-medium text-emerald-700">Immediate (100%)</span>
                  </div>
                ) : <VestingScheduleSelector label="NEC vesting" schedule={vesting.necSchedule} onChange={onNecScheduleChange} />)}
                {profitSharingEnabled && <VestingScheduleSelector label="Profit sharing vesting" schedule={vesting.profitSharingSchedule} onChange={onPsScheduleChange} />}
              </>
            )}
          </div>
        )}
      </CardContent>}
    </PlanCardShell>
  )
}
